// Why a guest can no longer change a pre-order (FOYR-432).
//
// The API says so in two places, and both are additive:
//
//   - a guest read of the reservation carries
//       editLock: { locked: boolean, reason: "time" | "sent_to_pos" | null,
//                   lockAt: string | null }
//   - the guest routes' existing 403 for a closed window carries
//       lockReason: "time" | "sent_to_pos"
//
// "time": the lock instant (`lockAt`, the seating time plus the restaurant's
// `lockOffsetMinutes`) has passed. "sent_to_pos": the restaurant sent the
// order to its POS, which locks it at once whatever the clock says.
//
// A backend that predates this sends neither field. Everything here treats
// both as optional and then does what the app did before: lock when the
// local clock passes `expiresAtMs`, and say nothing about why.

export type LockReason = "time" | "sent_to_pos";

export interface EditLock {
  readonly locked: boolean;
  readonly reason: LockReason | null;
  readonly lockAt: string | null;
}

/** What the guest is told when the POS send locked the order. */
export const SENT_TO_POS_MESSAGE =
  "The restaurant has sent your order to the kitchen — it can no longer be changed.";

/** What the guest is told when the lock is time-based but we can't say how long before. */
export const WINDOW_CLOSED_MESSAGE = "Your pre-order window has closed.";

const MS_PER_MINUTE = 60_000;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

export const parseLockReason = (value: unknown): LockReason | null =>
  value === "time" || value === "sent_to_pos" ? value : null;

/** The `editLock` off a guest read, or undefined when the backend sent none (or something unusable). */
export const parseEditLock = (value: unknown): EditLock | undefined => {
  if (!isRecord(value) || typeof value.locked !== "boolean") return undefined;
  return {
    locked: value.locked,
    reason: parseLockReason(value.reason),
    lockAt:
      typeof value.lockAt === "string" && value.lockAt !== ""
        ? value.lockAt
        : null,
  };
};

/**
 * The `lockReason` of a guest route's 403, or null for any other failure
 * (including a 403 from an older backend, which has no such field).
 */
export const lockReasonOf = (error: unknown): LockReason | null => {
  if (!isRecord(error) || !isRecord(error.response)) return null;
  const { status, data } = error.response;
  if (status !== 403 || !isRecord(data)) return null;
  return parseLockReason(data.lockReason);
};

/** The slice of the reservation state a lock decision reads. */
export interface LockClues {
  readonly expiresAtMs?: number;
  readonly lockAtMs?: number;
  readonly reservationAtIso?: string;
  readonly editLock?: EditLock;
}

const toMs = (iso: string | null | undefined): number | null => {
  if (!iso) return null;
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? ms : null;
};

/**
 * Whether edits are closed: the server says so, or the local clock has passed
 * the lock instant. The server's `editLock.lockAt` is that instant when valid;
 * `expiresAtMs` (the behaviour before `editLock` existed) is the fallback.
 */
export const isEditLocked = (
  clues: Pick<LockClues, "expiresAtMs" | "editLock">,
  now: number = Date.now(),
): boolean => {
  if (clues.editLock?.locked === true) return true;
  const serverLockAt = toMs(clues.editLock?.lockAt);
  if (serverLockAt !== null && serverLockAt <= now) return true;
  return Boolean(clues.expiresAtMs && clues.expiresAtMs <= now);
};

/**
 * Whole minutes between the lock instant and the reservation, or null when
 * either is unknown or the lock isn't before the reservation (an offset of 0
 * locks at seating, which "0 minutes before" would describe badly).
 */
export const minutesLockedBeforeReservation = (
  clues: Pick<LockClues, "editLock" | "lockAtMs" | "reservationAtIso">,
): number | null => {
  const lockAt =
    toMs(clues.editLock?.lockAt) ??
    (typeof clues.lockAtMs === "number" && Number.isFinite(clues.lockAtMs)
      ? clues.lockAtMs
      : null);
  const reservationAt = toMs(clues.reservationAtIso);
  if (lockAt === null || reservationAt === null) return null;
  const minutes = Math.round((reservationAt - lockAt) / MS_PER_MINUTE);
  return minutes > 0 ? minutes : null;
};

/** The sentence for a lock. `reason` null (a lock with no stated cause) gets the plain "closed" line. */
export const describeLock = (
  reason: LockReason | null,
  clues: Pick<LockClues, "editLock" | "lockAtMs" | "reservationAtIso"> = {},
): string => {
  if (reason === "sent_to_pos") return SENT_TO_POS_MESSAGE;
  if (reason === "time") {
    const minutes = minutesLockedBeforeReservation(clues);
    if (minutes !== null) {
      return `Orders lock ${minutes} ${minutes === 1 ? "minute" : "minutes"} before your reservation.`;
    }
  }
  return WINDOW_CLOSED_MESSAGE;
};

/**
 * The line to show the guest when edits are closed, or null when the app
 * can't say why. A server-reported lock gives its own reason; a valid server
 * lock instant that has passed (the snapshot said open, but the clock has
 * since reached `editLock.lockAt`) is a time lock. A lock only the legacy
 * local clock (`expiresAtMs`) knows about stays silent, as it always has.
 */
export const lockMessageFor = (
  clues: Pick<LockClues, "editLock" | "lockAtMs" | "reservationAtIso">,
  now: number = Date.now(),
): string | null => {
  const lock = clues.editLock;
  if (!lock) return null;
  if (lock.locked) return describeLock(lock.reason, clues);
  const lockAt = toMs(lock.lockAt);
  return lockAt !== null && lockAt <= now ? describeLock("time", clues) : null;
};
